import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  Trophy,
  BarChart3,
  Upload,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowRight,
  Sparkles,
  User,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { trackEvent } from '@/lib/gtag';

const API_URL = import.meta.env.VITE_API_URL || 'https://lionfish-app-zwr6q.ondigitalocean.app/api/v1';
const BATTLE_TIMEOUT_MS = 90000; // 90 seconds — enough for async detectors (Decopy/TruthScan poll up to 60s)

interface DetectorResult {
  id: string;
  name: string;
  slug: string;
  url: string;
  score: number;
  label: 'AI' | 'HUMAN' | 'UNCERTAIN';
  isCorrect: boolean;
  eloBefore: number;
  eloAfter: number;
  eloChange: number;
}

interface BattleResult {
  success: boolean;
  groundTruth: 'AI' | 'HUMAN';
  detectorA: DetectorResult;
  detectorB: DetectorResult;
  winner: 'A' | 'B' | 'BOTH' | 'NONE';
  voteId: string;
}

type Step = 'upload' | 'ground-truth' | 'loading' | 'results';

export default function Arena() {
  const [step, setStep] = useState<Step>('upload');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [_groundTruth, setGroundTruth] = useState<'AI' | 'HUMAN' | null>(null);
  const [result, setResult] = useState<BattleResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [totalVotes, setTotalVotes] = useState<number>(0);
  const [loadingSeconds, setLoadingSeconds] = useState<number>(0);

  // Fetch total votes on mount
  useEffect(() => {
    fetch(`${API_URL}/arena/leaderboard`)
      .then(res => res.json())
      .then(data => setTotalVotes(data.totalVotes || 0))
      .catch(() => {});
  }, []);

  // Loading timer
  useEffect(() => {
    if (step !== 'loading') {
      setLoadingSeconds(0);
      return;
    }
    const interval = setInterval(() => {
      setLoadingSeconds(s => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [step]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setImageFile(file);
      trackEvent('file_upload', {
        file_type: file.type,
        file_size: file.size,
        context: 'arena',
      });
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setStep('ground-truth');
      setError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const submitBattle = async (truth: 'AI' | 'HUMAN') => {
    setStep('loading');
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), BATTLE_TIMEOUT_MS);

    try {
      const formData = new FormData();
      formData.append('image', imageFile!);
      formData.append('groundTruth', truth);
      formData.append('sessionId', getSessionId());

      const response = await fetch(`${API_URL}/arena/battle`, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error('Server returned an invalid response. Please try again.');
      }

      if (!response.ok) {
        throw new Error(data.error || 'Battle failed. Please try again.');
      }

      setResult(data);
      setStep('results');
      setTotalVotes(prev => prev + 1);

      trackEvent('arena_battle_submit', {
        winner: data.winner,
        ground_truth: data.groundTruth,
        detector_a: data.detectorA.name,
        detector_b: data.detectorB.name,
      });

      trackEvent('detection_result', {
        detector: data.detectorA.name,
        label: data.detectorA.label,
        score: data.detectorA.score,
        is_correct: data.detectorA.isCorrect,
      });
      trackEvent('detection_result', {
        detector: data.detectorB.name,
        label: data.detectorB.label,
        score: data.detectorB.score,
        is_correct: data.detectorB.isCorrect,
      });
    } catch (err) {
      clearTimeout(timeoutId);
      let message: string;
      if (err instanceof Error && err.name === 'AbortError') {
        message = 'Request timed out. The detectors are taking too long — please try again.';
      } else if (err instanceof TypeError && err.message === 'Failed to fetch') {
        message = 'Network error — please check your connection and try again.';
      } else {
        message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      }
      setError(message);
      setStep('ground-truth');
    }
  };

  const handleGroundTruthSelect = async (truth: 'AI' | 'HUMAN') => {
    setGroundTruth(truth);
    trackEvent('arena_vote', { ground_truth: truth });
    await submitBattle(truth);
  };

  const handleRetry = () => {
    if (_groundTruth) {
      submitBattle(_groundTruth);
    }
  };

  const handleReset = () => {
    setStep('upload');
    setImageFile(null);
    setImagePreview(null);
    setGroundTruth(null);
    setResult(null);
    setError(null);
  };

  // Get or create session ID
  const getSessionId = () => {
    let sessionId = localStorage.getItem('arena_session_id');
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      localStorage.setItem('arena_session_id', sessionId);
    }
    return sessionId;
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Trophy className="w-6 h-6 text-yellow-500" />
                AI Detector Arena
              </h1>
              <p className="text-gray-400 mt-1">
                Test which AI detector performs better on your images
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-2xl font-bold text-purple-400">{totalVotes}</div>
                <div className="text-xs text-gray-500">total battles</div>
              </div>
              <Link
                to="/"
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
              >
                <BarChart3 className="w-4 h-4" />
                Rankings
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {['Upload', 'Label', 'Battle'].map((label, idx) => {
            const stepIdx = step === 'upload' ? 0 : step === 'ground-truth' ? 1 : 2;
            const isActive = idx <= stepIdx;
            const isCurrent = idx === stepIdx;
            return (
              <div key={label} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    isActive
                      ? isCurrent
                        ? 'bg-purple-600 text-white'
                        : 'bg-purple-600/50 text-purple-200'
                      : 'bg-gray-800 text-gray-500'
                  }`}
                >
                  {idx + 1}
                </div>
                <span
                  className={`ml-2 text-sm ${
                    isActive ? 'text-white' : 'text-gray-500'
                  }`}
                >
                  {label}
                </span>
                {idx < 2 && (
                  <div
                    className={`w-12 h-0.5 mx-3 ${
                      idx < stepIdx ? 'bg-purple-600' : 'bg-gray-800'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-red-400">{error}</p>
              {_groundTruth && step === 'ground-truth' && (
                <button
                  onClick={handleRetry}
                  className="mt-3 flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-sm font-medium transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Retry Battle
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-purple-500 bg-purple-500/10'
                : 'border-gray-700 hover:border-purple-500 bg-gray-900/50'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="w-12 h-12 mx-auto mb-4 text-gray-500" />
            <p className="text-lg font-medium mb-2">
              {isDragActive ? 'Drop your image here' : 'Upload an image to test'}
            </p>
            <p className="text-gray-500 text-sm">
              Drag & drop or click to select. Max 10MB. JPEG, PNG, WebP supported.
            </p>
          </div>
        )}

        {/* Step 2: Ground Truth Selection */}
        {step === 'ground-truth' && (
          <div className="space-y-6">
            {/* Image preview */}
            {imagePreview && (
              <div className="rounded-xl overflow-hidden bg-gray-900 border border-gray-800">
                <img
                  src={imagePreview}
                  alt="Uploaded"
                  className="w-full max-h-[400px] object-contain mx-auto"
                />
              </div>
            )}

            {/* Ground truth selection */}
            <div className="text-center">
              <h2 className="text-xl font-bold mb-2">Is this image AI-generated?</h2>
              <p className="text-gray-400 mb-6">
                Tell us the ground truth so we can evaluate detector accuracy
              </p>

              <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                <button
                  onClick={() => handleGroundTruthSelect('AI')}
                  className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-gray-700 hover:border-purple-500 bg-gray-900/50 transition-all hover:bg-purple-500/10"
                >
                  <Sparkles className="w-10 h-10 text-purple-400" />
                  <span className="font-medium text-lg">AI Generated</span>
                  <span className="text-gray-500 text-sm">Made by AI model</span>
                </button>

                <button
                  onClick={() => handleGroundTruthSelect('HUMAN')}
                  className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-gray-700 hover:border-purple-500 bg-gray-900/50 transition-all hover:bg-purple-500/10"
                >
                  <User className="w-10 h-10 text-blue-400" />
                  <span className="font-medium text-lg">Real Photo</span>
                  <span className="text-gray-500 text-sm">Human captured</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Loading */}
        {step === 'loading' && (
          <div className="text-center py-12">
            <Loader2 className="w-16 h-16 mx-auto mb-6 text-purple-500 animate-spin" />
            <h2 className="text-xl font-bold mb-2">Running Battle...</h2>
            <p className="text-gray-400">
              Testing two random AI detectors on your image
            </p>
            {loadingSeconds > 0 && (
              <p className="text-gray-600 text-sm mt-3">
                {loadingSeconds}s elapsed
                {loadingSeconds >= 10 && ' — some detectors take longer to respond'}
              </p>
            )}
          </div>
        )}

        {/* Step 4: Results */}
        {step === 'results' && result && (
          <div className="space-y-6">
            {/* Image preview small */}
            <div className="flex items-center gap-4 p-4 bg-gray-900 rounded-xl border border-gray-800">
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Test image"
                  className="w-20 h-20 object-cover rounded-lg"
                />
              )}
              <div>
                <div className="text-sm text-gray-500">Ground truth:</div>
                <div className={`font-bold text-lg ${
                  result.groundTruth === 'AI' ? 'text-purple-400' : 'text-blue-400'
                }`}>
                  {result.groundTruth === 'AI' ? 'AI Generated' : 'Real Photo'}
                </div>
              </div>
            </div>

            {/* Detector results */}
            <div className="grid md:grid-cols-2 gap-6">
              {[result.detectorA, result.detectorB].map((detector) => (
                <div
                  key={detector.id}
                  className={`relative rounded-xl border-2 p-6 transition-all ${
                    detector.isCorrect
                      ? 'border-green-500/50 bg-green-500/5'
                      : 'border-red-500/50 bg-red-500/5'
                  }`}
                >
                  {/* Badge */}
                  <div className={`absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                    detector.isCorrect
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {detector.isCorrect ? (
                      <>
                        <CheckCircle className="w-3 h-3" />
                        Correct
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3" />
                        Wrong
                      </>
                    )}
                  </div>

                  {/* Detector name */}
                  <a
                    href={detector.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg font-bold hover:text-purple-400 transition-colors"
                  >
                    {detector.name}
                  </a>

                  {/* Score */}
                  <div className="mt-4 text-center">
                    <div className={`text-5xl font-bold mb-2 ${
                      detector.score >= 0.7 ? 'text-purple-400' :
                      detector.score >= 0.3 ? 'text-yellow-400' : 'text-blue-400'
                    }`}>
                      {Math.round(detector.score * 100)}%
                    </div>
                    <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      detector.label === 'AI' ? 'bg-purple-500/20 text-purple-400' :
                      detector.label === 'HUMAN' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      Predicted: {detector.label === 'AI' ? 'AI Generated' :
                       detector.label === 'HUMAN' ? 'Real Photo' : 'Uncertain'}
                    </div>
                  </div>

                  {/* Elo change */}
                  <div className="mt-4 flex justify-between text-sm">
                    <span className="text-gray-500">Elo Rating</span>
                    <span className={detector.eloChange >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {detector.eloAfter} ({detector.eloChange >= 0 ? '+' : ''}{detector.eloChange})
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Winner announcement */}
            <div className="text-center p-4 bg-gray-900 rounded-xl border border-gray-800">
              {result.winner === 'BOTH' && (
                <p className="text-green-400 font-medium">Both detectors correctly identified the image!</p>
              )}
              {result.winner === 'NONE' && (
                <p className="text-red-400 font-medium">Both detectors got it wrong!</p>
              )}
              {result.winner === 'A' && (
                <p className="text-purple-400 font-medium">{result.detectorA.name} wins this round!</p>
              )}
              {result.winner === 'B' && (
                <p className="text-purple-400 font-medium">{result.detectorB.name} wins this round!</p>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex justify-center gap-4">
              <button
                onClick={handleReset}
                className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
              >
                Try Another Image
                <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/"
                className="flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
              >
                <BarChart3 className="w-4 h-4" />
                View Rankings
              </Link>
            </div>
          </div>
        )}

        {/* How it works */}
        {step === 'upload' && (
          <div className="mt-12 text-center">
            <h3 className="text-lg font-bold mb-4">How it works</h3>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-800">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-sm font-bold mb-3">1</div>
                <h4 className="font-medium mb-1">Upload Image</h4>
                <p className="text-sm text-gray-500">Upload any image you want to test</p>
              </div>
              <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-800">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-sm font-bold mb-3">2</div>
                <h4 className="font-medium mb-1">Provide Ground Truth</h4>
                <p className="text-sm text-gray-500">Tell us if it's AI-generated or a real photo</p>
              </div>
              <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-800">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-sm font-bold mb-3">3</div>
                <h4 className="font-medium mb-1">See Results</h4>
                <p className="text-sm text-gray-500">Watch two random detectors battle it out</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
